import {
  Component,
  Input,
  Output,
  EventEmitter,
  ContentChildren,
  QueryList,
  ElementRef,
  Renderer2,
} from '@angular/core';

import { TabComponent } from './tab.component';

/**
 * Container component that manages a group of {@link TabComponent} instances and handles selection.
 *
 * @remarks
 * TabGroupComponent displays a set of tabs and manages user interaction. It provides keyboard
 * navigation support (arrow keys) for switching between tabs, automatically skipping disabled tabs.
 * When a tab is selected, the component emits the new selected index via the `select` output.
 */
@Component({
  selector: 'bb-tab-group-ui',
  templateUrl: './tab-group.component.html',
  standalone: false,
})
export class TabGroupComponent {
  constructor(
    private el: ElementRef,
    private renderer2: Renderer2,
  ) {}
  /**
   * Sets the initially selected tab by index.
   *
   * @remarks
   * This input determines which tab is selected when the component first loads. If not specified
   * or set to an invalid value, the first tab (index 0) is selected by default.
   *
   * @param initialSelection The index of the tab to select initially. If `undefined`, defaults to 0.
   *
   * @example
   *
   * ```html
   * <bb-tab-group-ui [initialSelection]="2">
   *   <bb-tab-ui><span>Tab 1</span></bb-tab-ui>
   *   <bb-tab-ui><span>Tab 2</span></bb-tab-ui>
   *   <bb-tab-ui><span>Tab 3</span></bb-tab-ui>
   * </bb-tab-group-ui>
   * ```
   */
  @Input()
  set initialSelection(initSelection: number | undefined) {
    this.activeIndexValue = initSelection || 0;
  }
  /**
   * Emits when the user selects a different tab.
   *
   * @remarks
   * This output event fires whenever the user clicks a tab or navigates to it using arrow keys.
   * The emitted value is the zero-based index of the newly selected tab.
   *
   * @example
   *
   * ```html
   * <bb-tab-group-ui (select)="onTabSelected($event)">
   *   <bb-tab-ui><span>Tab 1</span></bb-tab-ui>
   *   <bb-tab-ui><span>Tab 2</span></bb-tab-ui>
   * </bb-tab-group-ui>
   * ```
   */
  @Output() select = new EventEmitter<number>();
  /**
   * Query list of all {@link TabComponent} children in this group.
   *
   * @remarks
   * This is used internally to access tab properties such as the `disabled` state when
   * handling keyboard navigation or click events.
   */
  @ContentChildren(TabComponent) items: QueryList<TabComponent> | undefined;

  /**
   * Keeps track of currently active tab item index
   */
  private activeIndexValue = 0;

  /**
   * Gets the index of the currently active tab.
   *
   * @returns The zero-based index of the selected tab.
   */
  get activeIndex() {
    return this.activeIndexValue;
  }

  /**
   * Handles keyboard navigation between tabs using arrow keys.
   *
   * @remarks
   * Responds to Left and Right arrow key presses to move focus between tabs. The component
   * automatically skips disabled tabs and wraps around when reaching either end of the tab list.
   * Focus management is handled via `blur()` and `focus()` on the respective tab elements.
   *
   * @param event The keyboard event triggered by user input.
   * @param index The current tab index when the event was triggered.
   *
   * @example
   *
   * ```html
   * <button
   *   role="tab"
   *   (keydown)="onKeyDown($event, $index)"
   * >
   *   Tab Button
   * </button>
   * ```
   */
  onKeyDown(event: KeyboardEvent, index: number) {
    if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') {
      return;
    }
    const tabList = this.el.nativeElement.querySelector('[role="tablist"]');
    let tabItemIndex = index;
    const numberOfChildren = tabList.children.length;
    if (event.key === 'ArrowRight') {
      tabItemIndex++;
      tabItemIndex = this.getIndexOfNextTab(tabItemIndex, numberOfChildren);
    }
    if (event.key === 'ArrowLeft') {
      tabItemIndex--;
      tabItemIndex = this.getIndexOfPreviousTab(tabItemIndex, numberOfChildren);
    }

    const nextTabEl = this.el.nativeElement.querySelectorAll('[role="tab"]')[tabItemIndex];
    const currentTabEl = this.el.nativeElement.querySelector('[aria-selected="true"]');
    // Remove focus from current element
    currentTabEl.blur();
    // Set focus to the next/previous element
    nextTabEl.focus();
  }

  /**
   * Finds the next enabled tab index, wrapping around if necessary.
   *
   * @remarks
   * This private helper method is used during keyboard navigation to skip disabled tabs.
   * If all remaining tabs in the forward direction are disabled, the search wraps to the
   * beginning of the tab list.
   *
   * @param tabItemIndex The starting tab index.
   * @param numberOfChildren The total number of tabs.
   * @returns The index of the next enabled tab.
   */
  private getIndexOfNextTab(tabItemIndex: number, numberOfChildren: number) {
    // If next tab is disabled
    if (this.items?.get(tabItemIndex)?.disabled) {
      // Then check all the next tabs until finding one that is not disabled and return the index
      for (let i = tabItemIndex + 1; i <= numberOfChildren; i++) {
        // If last tab is disabled then start checking from the beginning until finding the next enabled tab
        if (i >= numberOfChildren) i = 0;
        if (!this.items?.get(i)?.disabled) {
          tabItemIndex = i;
          break;
        }
      }
    }
    // If index is greater than or equal to length then assign the index of the first tab
    if (tabItemIndex >= numberOfChildren) tabItemIndex = 0;

    return tabItemIndex;
  }

  /**
   * Finds the previous enabled tab index, wrapping around if necessary.
   *
   * @remarks
   * This private helper method is used during keyboard navigation to skip disabled tabs.
   * If all remaining tabs in the backward direction are disabled, the search wraps to the
   * end of the tab list.
   *
   * @param tabItemIndex The starting tab index.
   * @param numberOfChildren The total number of tabs.
   * @returns The index of the previous enabled tab.
   */
  private getIndexOfPreviousTab(tabItemIndex: number, numberOfChildren: number) {
    // If index is less than 0 then assign the index of the last tab
    if (tabItemIndex < 0) tabItemIndex = numberOfChildren - 1;
    // If previous tab is disabled
    if (this.items?.get(tabItemIndex)?.disabled) {
      // Then check all the previous tabs until finding one that is not disabled and return the index
      for (let i = tabItemIndex - 1; i >= -1; i--) {
        // If first tab is disabled then start checking from last tab until finding the next enabled tab
        if (i < 0) i = numberOfChildren - 1;
        if (!this.items?.get(i)?.disabled) {
          tabItemIndex = i;
          break;
        }
      }
    }

    return tabItemIndex;
  }
  /**
   * Handles tab selection when user clicks a tab button.
   *
   * @remarks
   * This internal handler prevents default click behavior and updates the active tab index.
   * If the clicked tab is already selected, the method returns early without emitting an event.
   * Otherwise, it updates the active index and emits the `select` output with the new index.
   *
   * @param event The mouse click event.
   * @param index The index of the tab being clicked.
   *
   * @example
   *
   * ```html
   * <button
   *   role="tab"
   *   (click)="onClick($event, $index)"
   * >
   *   Tab Button
   * </button>
   * ```
   */
  onClick(event: MouseEvent, index: number) {
    event.preventDefault();

    if (this.activeIndex === index) {
      return;
    }

    this.activeIndexValue = index;
    this.select.emit(index);
  }
}
